import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { Params, RouterModule } from '@angular/router';
import { Product } from '@app/akita/api/products/models/product.model';
import { AkitaRouterQuery } from '@app/akita/router/state/router.query';
import { AkitaScreenQuery } from '@app/akita/screen/state/screen.query';
import { ScreenSizeInfoState } from '@app/akita/screen/state/screen.store';
import { ArrowRight1IconComponent } from '@app/shared/components/icons/generated/arrow-right-1/arrow-right-1.component';
import { toUrlSlug } from '@app/shared/utils/url.utils';
import { TranslocoModule } from '@ngneat/transloco';
import { Observable } from 'rxjs';

@Component({
  selector: 'app-featured-breadcrumbs',
  templateUrl: './featured-breadcrumbs.template.html',
  styleUrls: ['./featured-breadcrumbs.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TranslocoModule, RouterModule, ArrowRight1IconComponent],
})
export class FeaturedBreadcrumbsComponent implements OnChanges {
  @Input() public product?: Product | null;
  @Input() public collectionMode?: boolean = false;
  @Input() public category?: string | null;
  @Input() public brand?: string | null;
  @Input() public model?: string | null;

  public readonly lessThan$: Observable<ScreenSizeInfoState>;
  public queryParamsToKeep$: Observable<Params>;

  public readonly isRtl$: Observable<boolean>;

  constructor(
    private readonly akitaRouterQuery: AkitaRouterQuery,
    private readonly akitaScreenQuery: AkitaScreenQuery
  ) {
    this.isRtl$ = this.akitaRouterQuery.selectIsRtlLayout();
    this.lessThan$ = this.akitaScreenQuery.selectLessThan();
    this.queryParamsToKeep$ = this.akitaRouterQuery.selectQueryParamsToKeep();
  }
  public ngOnChanges(changes: SimpleChanges): void {
    if (changes) {
      this.queryParamsToKeep$ = this.akitaRouterQuery.selectQueryParamsToKeep();
    }
  }

  public capitalizeCategory(str: string): string | null {
    return str
      .charAt(0)
      .toUpperCase()
      .concat(str.slice(1).replace(/ /g, '_').toLowerCase());
  }

  public getSearchUrlFragment(): Array<string> | string {
    return this.akitaRouterQuery.getPhoneSearchFragment();
  }

  public getCategoryCollectionFragment(cat: string): Array<string> | string {
    const fragment = this.akitaRouterQuery.homeUrlFragment;
    fragment.push('c');
    fragment.push('category');
    fragment.push(cat.toLowerCase());
    return fragment;
  }

  public getBrandCollectionFragment(
    brand: string,
    cat?: string | null
  ): Array<string> | string {
    const fragment = this.akitaRouterQuery.homeUrlFragment;
    fragment.push('c');
    if (!cat) {
      fragment.push('brand');
      fragment.push(brand.toLowerCase());
    } else {
      fragment.push('category');
      fragment.push(cat.toLowerCase() + `?brand=${brand.toLowerCase()}`);
    }
    return fragment;
  }

  public getModelCollectionFragment(cat: string): Array<string> | string {
    const fragment = this.akitaRouterQuery.homeUrlFragment;
    fragment.push('c');
    fragment.push('model');
    fragment.push(toUrlSlug(cat));
    return fragment;
  }

  public getHomeUrl(): string {
    return '/';
  }

  public getCategoryParams(category: string): Params | null {
    return {
      ...this.akitaRouterQuery.queryParamsToKeep,
      category: category,
    };
  }

  public getBrandParams(brand: string): Params | null {
    return {
      ...this.akitaRouterQuery.queryParamsToKeep,
      brands: brand,
    };
  }

  public getModelParams(model: string): Params | null {
    return {
      ...this.akitaRouterQuery.queryParamsToKeep,
      models: model,
    };
  }
}
